// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@tps/MobiusOverrides/Button/Button";
import { XngCustomGroup } from "@tps/XDL/constants";
import { getSystemColor } from "@tps/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";

const enum fields {
    backgroundColor = "backgroundColor",
    title = "title",
    description = "description",
    buttonText = "buttonText",
    buttonUrl = "buttonUrl",
    buttonVariant = "variant",
    openInNewTab = "openInNewTab",
    paddingSize = "paddingSize",
    mobilePaddingSize = "mobilePaddingSize",
    borderColor = "borderColor",
    borderSide = "borderSide",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonText]: string;
        [fields.buttonUrl]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary";
        [fields.openInNewTab]: boolean;
        [fields.paddingSize]: number;
        [fields.mobilePaddingSize]: number;
        [fields.borderColor]: string;
        [fields.borderSide]: "left" | "right";
    };
}

type Props = OwnProps;

interface EspotCardStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const espotCardStyles: EspotCardStyles = {};

const EspotCard: React.FC<Props> = ({ fields }) => {
    const styles = espotCardStyles;
    const history = useHistory();
    const link = useGetLink(fields.buttonUrl ?? { type: "Page", value: "" });
    const onClickHandler = () => {
        if (!link.url) {
            return;
        }
        if (fields.openInNewTab) {
            window.open(link.url, "_blank");
        } else {
            history.push(link.url);
        }
    };

    return (
        <StyledWrapper
            {...styles.wrapper}
            {...{
                backgroundColor: fields.backgroundColor,
                padding: fields.paddingSize,
                mobilePadding: fields.mobilePaddingSize,
                borderColor: fields.borderColor,
                borderSide: fields.borderSide,
            }}
        >
            {fields.title && <Typography {...styles.title}>{parse(fields.title, parserOptions)}</Typography>}
            {fields.description && (
                <Typography {...styles.description}>{parse(fields.description, parserOptions)}</Typography>
            )}
            {fields.buttonText && fields.buttonUrl && (
                <StyledWrapper {...styles.buttonWrapper}>
                    <Button onClick={onClickHandler} variant={fields.variant} aria-label={fields.buttonText}>
                        {fields.buttonText}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: EspotCard,
    definition: {
        group: XngCustomGroup,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: getSystemColor("common.ui2"),
            },
            {
                fieldType: "General",
                name: fields.title,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "General",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "Translatable",
                name: fields.buttonText,
                editorTemplate: "TextField",
                displayName: "Button Text",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonUrl,
                displayName: "Button Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.openInNewTab,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderSide,
                displayName: "Border Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left (Top on Mobile)", value: "left" },
                    { displayName: "Right (Bottom on Mobile)", value: "right" },
                ],
                defaultValue: "",
                fieldType: "General",
                isVisible: (item: HasFields) => !!item.fields.borderColor,
            },
            {
                name: fields.paddingSize,
                displayName: "Desktop Padding Size in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: 32,
                fieldType: "General",
                min: 0,
            },
            {
                name: fields.mobilePaddingSize,
                displayName: "Mobile Padding Size",
                editorTemplate: "IntegerField",
                defaultValue: 32,
                fieldType: "General",
                min: 0,
            },
        ],
    },
};

export default widgetModule;
